import { Link } from "@tanstack/react-router";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

import { Transaction } from "../../../services/queries/transactions/types";
import { TransactionStatus, routePaths } from "../../../constants";
import { formatDate } from "../../../utils";
import { formatMoney } from "../../../utils/money";

interface RecentActivityProps {
  transactions: Transaction[];
  loading?: boolean;
}

function StatusPill({ status }: { status: TransactionStatus }) {
  const isDone = status === TransactionStatus.COMPLETED;
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
        isDone ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
      }`}
    >
      {isDone ? "Completed" : "Pending"}
    </span>
  );
}

function ActivityRow({ transaction }: { transaction: Transaction }) {
  const { institution, accountName, baseCurrency, quoteCurrency, baseAmount } =
    transaction;

  return (
    <div className="flex items-center gap-3 py-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary">
        <PaperAirplaneIcon className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {institution || "Transfer"}
          {accountName ? ` · ${accountName}` : ""}
        </p>
        <p className="text-xs text-gray-400">
          {baseCurrency} → {quoteCurrency} · {formatDate(transaction.created)}
        </p>
      </div>

      <div className="flex flex-col items-end gap-1">
        <span className="text-sm font-semibold text-gray-900">
          {formatMoney(baseAmount, baseCurrency)}
        </span>
        <StatusPill status={transaction.status} />
      </div>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="h-10 w-10 shrink-0 rounded-full animate-shimmer bg-1000px bg-shimmer" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-40 rounded animate-shimmer bg-1000px bg-shimmer" />
        <div className="h-3 w-24 rounded animate-shimmer bg-1000px bg-shimmer" />
      </div>
      <div className="h-4 w-16 rounded animate-shimmer bg-1000px bg-shimmer" />
    </div>
  );
}

function EmptyActivity() {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary">
        <PaperAirplaneIcon className="h-6 w-6" />
      </span>
      <p className="mt-3 text-sm font-medium text-gray-700">
        No transactions yet
      </p>
      <p className="mt-1 text-xs text-gray-400">
        Your first transfer will show up here.
      </p>
      <Link
        to={routePaths.send}
        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors"
      >
        <PaperAirplaneIcon className="h-4 w-4" />
        Send money
      </Link>
    </div>
  );
}

export default function RecentActivity({
  transactions,
  loading = false,
}: RecentActivityProps) {
  const recent = transactions.slice(0, 5);

  return (
    <div className="rounded-2xl bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">
          Recent activity
        </h2>
        <Link
          to={routePaths.transactions}
          className="text-sm font-medium text-primary hover:underline"
        >
          View all ›
        </Link>
      </div>

      <div className="mt-2 divide-y divide-gray-100">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <ActivitySkeleton key={index} />
          ))
        ) : recent.length === 0 ? (
          <EmptyActivity />
        ) : (
          recent.map((transaction) => (
            <ActivityRow key={transaction.id} transaction={transaction} />
          ))
        )}
      </div>
    </div>
  );
}
